/* assets/pw-product-westin.js */
(function () {
  'use strict';

  /**
   * Minimal debounce helper — this file is loaded as a classic (non-module)
   * script, so we can't `import { debounce } from '@theme/utilities'` the
   * way the card's product-form-mnm.js does. This keeps the same behavior
   * (collapse rapid +/- clicks into a single network call) without needing
   * to change the script tag to type="module".
   */
  function debounce(fn, wait) {
    let timeoutId;
    return function debounced(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class ProductWestin extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productHandle = this.dataset.productHandle;

      this.refs = {};
      this.querySelectorAll('[ref]').forEach((el) => {
        this.refs[el.getAttribute('ref')] = el;
      });

      this.variants = null; // fetched lazily on first option change
      this.selectedOptions = this.getInitialSelectedOptions();

      // Debounced live cart sync for the quantity stepper — same idea as
      // the card's #debouncedQuantityChange, just scoped to this element.
      this.debouncedSyncQuantity = debounce((quantity) => this.syncCartQuantity(quantity), 300);

      this.bindThumbnails();
      this.bindZoom();
      this.bindOptions();
      this.bindQuantity();
      this.bindForm();
    }

    getInitialSelectedOptions() {
      const selected = [];
      this.querySelectorAll('.pw__option').forEach((group) => {
        const active = group.querySelector('.pw__option-btn.is-selected');
        selected.push(active ? active.dataset.value : null);
      });
      return selected;
    }

    /* ---------------- Gallery ---------------- */
    bindThumbnails() {
      const thumbs = this.querySelectorAll('.pw__thumb');
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          thumbs.forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
          const full = thumb.dataset.full;
          if (full) this.setMainImage(full);
        });
      });
    }

    setMainImage(url) {
      if (!this.refs.mainImage || !url) return;
      this.refs.mainImage.removeAttribute('srcset');
      this.refs.mainImage.removeAttribute('sizes');
      this.refs.mainImage.src = url;
    }

    bindZoom() {
      if (!this.refs.zoomButton) return;
      this.refs.zoomButton.addEventListener('click', () => {
        const src = this.refs.mainImage ? this.refs.mainImage.src : null;
        if (src) window.open(src, '_blank');
      });
    }

    /* ---------------- Variant options ---------------- */
    bindOptions() {
      const groups = this.querySelectorAll('.pw__option');
      if (!groups.length) return;

      groups.forEach((group) => {
        group.querySelectorAll('.pw__option-btn').forEach((btn) => {
          btn.addEventListener('click', async () => {
            if (btn.disabled) return;
            const index = Number(btn.dataset.optionIndex);
            group.querySelectorAll('.pw__option-btn').forEach((b) => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');
            this.selectedOptions[index] = btn.dataset.value;
            await this.updateVariant();
          });
        });
      });
    }

    async fetchVariants() {
      if (this.variants) return this.variants;
      const res = await fetch(`/products/${this.productHandle}.js`);
      const product = await res.json();
      this.variants = product.variants;
      return this.variants;
    }

    async updateVariant() {
      const variants = await this.fetchVariants();
      const match = variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return this.selectedOptions.every((val, i) => val == null || opts[i] === val);
      });

      if (!match) {
        this.setBuyable(false);
        return;
      }

      if (this.refs.variantIdInput) this.refs.variantIdInput.value = match.id;

      if (this.refs.price) this.refs.price.textContent = this.formatMoney(match.price);
      if (this.refs.comparePrice) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          this.refs.comparePrice.textContent = this.formatMoney(match.compare_at_price);
          this.refs.comparePrice.style.display = '';
        } else {
          this.refs.comparePrice.style.display = 'none';
        }
      }

      if (match.featured_image) {
        const url = match.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_1200x$1$2');
        this.setMainImage(url);
      }

      if (this.refs.qtyInput && match.quantity_rule) {
        const rule = match.quantity_rule;
        if (rule.min) this.refs.qtyInput.min = rule.min;
        if (rule.increment) this.refs.qtyInput.step = rule.increment;
        if (rule.min && Number(this.refs.qtyInput.value) < rule.min) {
          this.refs.qtyInput.value = rule.min;
        }
      }

      this.setBuyable(match.available);
      this.disableUnavailableCombos(variants);
    }

    disableUnavailableCombos(variants) {
      const groups = this.querySelectorAll('.pw__option');
      groups.forEach((group, index) => {
        group.querySelectorAll('.pw__option-btn').forEach((btn) => {
          const testOptions = [...this.selectedOptions];
          testOptions[index] = btn.dataset.value;
          const possible = variants.some((v) => {
            const opts = [v.option1, v.option2, v.option3];
            return testOptions.every((val, i) => val == null || opts[i] === val);
          });
          btn.disabled = !possible;
        });
      });
    }

    setBuyable(available) {
      [this.refs.addToCartButton, this.refs.buyNowButton].forEach((btn) => {
        if (!btn) return;
        btn.disabled = !available;
      });
      if (this.refs.addToCartText) {
        this.refs.addToCartText.textContent = available ? 'Add to Cart' : 'Sold Out';
      }
    }

    formatMoney(cents) {
      return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    /* ---------------- Quantity ---------------- */
    bindQuantity() {
      const { qtyInput, qtyMinus, qtyPlus } = this.refs;
      if (!qtyInput) return;

      const step = () => Number(qtyInput.step) || 1;
      const min = () => Number(qtyInput.min) || 1;

      qtyMinus?.addEventListener('click', () => {
        if (qtyMinus.disabled) return;
        const current = Number(qtyInput.value) || min();
        const next = Math.max(min(), current - step());
        qtyInput.value = next;
        this.debouncedSyncQuantity(next);
      });

      qtyPlus?.addEventListener('click', () => {
        if (qtyPlus.disabled) return;
        const current = Number(qtyInput.value) || min();
        const next = current + step();
        qtyInput.value = next;
        this.debouncedSyncQuantity(next);
      });

      qtyInput.addEventListener('change', () => {
        let val = Number(qtyInput.value);
        if (!val || val < min()) val = min();
        qtyInput.value = val;
        this.debouncedSyncQuantity(val);
      });
    }

    /**
     * Pushes the stepper's current quantity straight to the cart for the
     * selected variant — the same "click +/- and the cart just reflects it"
     * behavior as the card's live stepper. /cart/change.js will add the
     * line if it isn't already in the cart, update it if it is, or remove
     * it if the quantity lands on 0, so this works whether or not the
     * shopper has clicked "Add to Cart" yet.
     */
    async syncCartQuantity(quantity) {
      const variantId = this.refs.variantIdInput?.value;
      if (!variantId) return;

      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity }),
        });
        if (!res.ok) throw new Error('Update quantity failed');

        const cart = await res.json();
        await this.notifyThemeCartUpdate(cart);
      } catch (err) {
        console.error('[pw-product-westin] quantity update failed:', err);
      }
    }

    /* ---------------- Cart ---------------- */
    bindForm() {
      const form = this.querySelector('#pw-product-form') || this.querySelector('.pw__form');
      if (!form) return;

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart(form);
      });

      this.refs.buyNowButton?.addEventListener('click', async () => {
        const added = await this.addToCart(form, { silent: true });
        if (added) window.location.href = '/checkout';
      });
    }

    async addToCart(form, { silent } = {}) {
      this.setError('');
      const formData = new FormData(form);
      const btn = silent ? this.refs.buyNowButton : this.refs.addToCartButton;

      if (btn) {
        btn.disabled = true;
        if (!silent && this.refs.addToCartText) this.refs.addToCartText.textContent = 'Adding…';
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const data = await res.json();

        if (!res.ok) {
          this.setError(data.description || 'Could not add this item to your cart.');
          if (!silent && this.refs.addToCartText) this.refs.addToCartText.textContent = 'Add to Cart';
          return false;
        }

        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

        await this.notifyThemeCartUpdate(cart);

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, addedItem: data }, bubbles: true }));

        if (!silent && this.refs.addToCartText) {
          this.refs.addToCartText.textContent = 'Added ✓';
          setTimeout(() => {
            if (this.refs.addToCartText) this.refs.addToCartText.textContent = 'Add to Cart';
          }, 1800);
        }
        return true;
      } catch (err) {
        this.setError('Something went wrong. Please try again.');
        if (!silent && this.refs.addToCartText) this.refs.addToCartText.textContent = 'Add to Cart';
        return false;
      } finally {
        if (btn) btn.disabled = false;
      }
    }

    async notifyThemeCartUpdate(cart) {
      try {
        const { CartUpdateEvent } = await import('@theme/events');
        const event = new CartUpdateEvent(cart, this.sectionId || 'pw-product-westin', {
          itemCount: cart.item_count,
          source: 'pw-product-westin',
          sections: {},
        });
        document.dispatchEvent(event);
      } catch (err) {
        console.warn('pw-product-westin: falling back to manual cart count sync', err);
        this.syncCartCount(cart);
      }
    }

    syncCartCount(cart) {
      const count = cart.item_count;
      const selectors = [
        '[data-cart-count]',
        '[data-cart-item-count]',
        '.cart-count-bubble',
        '.cart-count',
        '.cart-link__bubble',
        '#cart-icon-bubble .visually-hidden',
        '#CartCount',
        '#CartCost',
        'a[href*="/cart"] .cart-count',
      ];
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          el.textContent = count;
        });
      });

      // Toggle any "empty cart" bubble states some themes use.
      document
        .querySelectorAll('[data-cart-count-bubble], .cart-count-bubble')
        .forEach((el) => el.classList.toggle('is-hidden', count === 0));
    }

    setError(message) {
      if (!this.refs.formError) return;
      this.refs.formError.textContent = message;
      this.refs.formError.hidden = !message;
    }
  }

  customElements.define('product-westin', ProductWestin);
})();